import type {
  ColumnConfig,
  ColumnsResponse,
  PreviewResponse,
} from '@app/application/report/column-editor/types'

import SFetch from '@app/utils/sfetch'

export async function fetchColumnsRequest(params: {
  reportId: number
  reportPeriodSetId: number | null
}): Promise<ColumnsResponse> {
  // A report can be viewed with a specific period set selected (see the
  // switcher in reports/_header_row.html.slim), so the drawer has to ask for
  // that set's columns instead of letting the server fall back to the active
  // one — otherwise it lists columns the table on screen isn't showing.
  const url = window.Silverfin.Routes.report_columns_path(
    window.Silverfin.currentFirmId,
    window.Silverfin.currentCompanyId,
    params.reportId,
    params.reportPeriodSetId
      ? { report_period_set_id: params.reportPeriodSetId }
      : {}
  )

  // Camelization is off so the schema's snake_case field keys and each column's
  // value keys line up with what the server sends.
  const response = await SFetch.json<ColumnsResponse>(
    url,
    {},
    { camelizeJSON: false }
  )
  if (!response) throw new Error('Empty report columns response')

  return response
}

// Asks what columns the report would render for the configs the drawer holds
// right now. Headers only — the periods a config expands into depend on the
// company's book years and which ledgers exist, so the server works them out.
export async function fetchColumnsPreviewRequest(params: {
  reportId: number
  columns: ColumnConfig[]
}): Promise<PreviewResponse> {
  const url = window.Silverfin.Routes.report_columns_preview_path(
    window.Silverfin.currentFirmId,
    window.Silverfin.currentCompanyId,
    params.reportId
  )

  // As with the columns request: no camelization on the way back, so the labels
  // and value keys come in as the presenter named them. (The request body is
  // decamelized regardless — see SFetch.json — which the snake_case field keys
  // the form schema ships come through unchanged.)
  const response = await SFetch.json<PreviewResponse>(
    url,
    { method: 'POST', body: { columns: params.columns } },
    { camelizeJSON: false }
  )
  if (!response) throw new Error('Empty report columns preview response')

  return response
}
